import type {PublicPlayer,RoomSnapshot} from '../shared/types';
import type {ViewContext} from './views';
const esc=(v:string)=>v.replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]!);
export function seatAvatar(ctx:ViewContext,player:PublicPlayer):string {
  const drag=ctx.playerId===ctx.room?.hostId && ctx.status==='online';
  return `<span class="seat-avatar" ${drag?'data-seat-drag title="拖动头像调整顺位 / Drag to reorder"':''}>${esc(player.nickname.slice(0,1))}<i class="${player.connected?'online':''}"></i></span>`;
}
export function seatShell(ctx:ViewContext,player:PublicPlayer,button:string):string {
  const host=ctx.playerId===ctx.room?.hostId;
  return `<div class="player-seat-shell" data-seat-id="${esc(player.id)}" ${host?'data-seat-owner':''}>${button}${host && player.id!==ctx.room?.hostId?`<button class="seat-remove" data-action="kick-player" data-player="${esc(player.id)}" aria-label="将 ${esc(player.nickname)} 踢出房间 / Remove player" title="踢出房间 / Remove player" ${ctx.status==='online'?'':'disabled'}>×</button>`:''}</div>`;
}
export function canReorderSeats(room:RoomSnapshot,ids:string[]):boolean {
  if(ids.length!==room.players.length || new Set(ids).size!==ids.length || ids.some(id=>!room.players.some(p=>p.id===id)))return false;
  const next=ids.find(id=>{
    const record=room.players.find(p=>p.id===id)!.stages[room.stageIndex];
    return room.phase==='setback'?!record?.setbackCompleted:room.phase==='questions'?!record?.questionsCompleted:false;
  }) ?? null;
  return next===room.turnPlayerId;
}
interface SeatState {room:RoomSnapshot|null;playerId:string|null;online:boolean}
interface Gesture {id:number;playerId:string;x:number;y:number;lastX:number;lastY:number;revision:number;order:string[];dragging:boolean;ghost:HTMLElement|null;offsetX:number;offsetY:number}
/** Only a local drag preview; the server commits the order on drop. */
export class SeatDrag {
  private gesture:Gesture|null=null;
  private pending:{ids:string[];revision:number}|null=null;
  private suppressUntil=0;
  constructor(private readonly root:HTMLElement,private readonly state:()=>SeatState,private readonly commit:(ids:string[],revision:number)=>void){
    root.addEventListener('pointerdown',event=>{
      // A new physical press is intentional; only the compatibility click
      // belonging to the drag itself should be suppressed.
      if(!this.gesture)this.suppressUntil=0;
      if(this.pending || event.button!==0 || !event.isPrimary || !(event.target as HTMLElement).closest('[data-seat-drag]'))return;
      const {room,playerId,online}=state();const seat=(event.target as HTMLElement).closest<HTMLElement>('[data-seat-id]');
      if(!room || !seat || !online || room.hostId!==playerId)return;
      const rect=seat.getBoundingClientRect();
      this.gesture={id:event.pointerId,playerId:seat.dataset.seatId!,x:event.clientX,y:event.clientY,lastX:event.clientX,lastY:event.clientY,revision:room.revision,order:room.players.map(p=>p.id),dragging:false,ghost:null,offsetX:event.clientX-rect.left,offsetY:event.clientY-rect.top};
    });
    root.addEventListener('pointermove',event=>{
      const g=this.gesture;if(!g || g.id!==event.pointerId)return;
      if(!this.valid()){this.cancel();return;}
      g.lastX=event.clientX;g.lastY=event.clientY;
      if(!g.dragging && Math.hypot(event.clientX-g.x,event.clientY-g.y)<7)return;
      if(!g.dragging)this.start();
      event.preventDefault();this.move();
    });
    root.addEventListener('pointerup',event=>{
      const g=this.gesture;if(!g || g.id!==event.pointerId)return;
      const shouldCommit=g.dragging && g.ghost?.dataset.blocked!=='true' && this.valid();const order=[...g.order],revision=g.revision;
      if(g.dragging){event.preventDefault();this.suppressUntil=Date.now()+450;}
      this.cancel();
      const room=state().room;
      if(shouldCommit && room && canReorderSeats(room,order) && order.some((id,i)=>id!==room.players[i]!.id)){
        this.pending={ids:order,revision};this.place(order);commit(order,revision);
      }
    });
    root.addEventListener('pointercancel',event=>{if(this.gesture?.id===event.pointerId)this.cancel();});
    // Touch starts with implicit capture on the avatar. Its bubbled loss is
    // expected when the stable root takes capture for a drag.
    root.addEventListener('lostpointercapture',event=>{if(event.target===root && this.gesture?.id===event.pointerId)this.cancel();});
    root.addEventListener('click',event=>{
      if(Date.now()<this.suppressUntil && event.detail>0 && (event.target as HTMLElement).closest('[data-seat-id]')){event.preventDefault();event.stopImmediatePropagation();}
    },true);
    root.addEventListener('keydown',event=>{
      if(event.key==='Escape'){this.cancel();return;}
      if(!event.altKey || !['ArrowLeft','ArrowRight'].includes(event.key))return;
      const {room,playerId,online}=state(),seat=(event.target as HTMLElement).closest<HTMLElement>('[data-seat-owner]');
      if(!room || !online || room.hostId!==playerId || !seat)return;
      event.preventDefault();const ids=room.players.map(p=>p.id),from=ids.indexOf(seat.dataset.seatId!),to=from+(event.key==='ArrowLeft'?-1:1);
      if(from<0 || to<0 || to>=ids.length)return;
      [ids[from],ids[to]]=[ids[to]!,ids[from]!];
      if(canReorderSeats(room,ids))commit(ids,room.revision);
    });
    root.ownerDocument.defaultView?.addEventListener('blur',()=>this.cancel());
  }
  refresh():void {
    if(this.pending){
      const s=this.state();
      if(s.online && s.room?.revision===this.pending.revision && s.playerId===s.room.hostId)this.place(this.pending.ids);
      else this.pending=null;
    }
    if(!this.gesture)return;
    if(!this.valid()){this.cancel();return;}
    if(this.gesture.dragging)this.place(this.gesture.order);
  }
  private valid():boolean {
    const s=this.state(),g=this.gesture;
    return !!g && !!s.room && s.online && s.playerId===s.room.hostId && s.room.revision===g.revision && s.room.players.some(p=>p.id===g.playerId);
  }
  private seats():HTMLElement[]{return [...this.root.querySelectorAll<HTMLElement>('[data-seat-id]')];}
  private start():void {
    const g=this.gesture!,source=this.seats().find(s=>s.dataset.seatId===g.playerId);if(!source)return;
    const rect=source.getBoundingClientRect(),ghost=source.cloneNode(true) as HTMLElement;
    ghost.classList.add('seat-drag-ghost');ghost.querySelector('.seat-remove')?.remove();ghost.setAttribute('aria-hidden','true');ghost.inert=true;
    ghost.style.width=`${rect.width}px`;ghost.style.height=`${rect.height}px`;
    this.root.ownerDocument.body.appendChild(ghost);g.ghost=ghost;g.dragging=true;
    this.root.setPointerCapture(g.id);this.place(g.order);
  }
  private move():void {
    const g=this.gesture!;if(!g.ghost)return;
    g.ghost.style.transform=`translate3d(${g.lastX-g.offsetX}px,${g.lastY-g.offsetY}px,0) scale(1.035)`;
    const seats=this.seats(),nav=seats[0]?.parentElement;if(!nav)return;
    const rect=nav.getBoundingClientRect();
    if(g.lastY<rect.top-50 || g.lastY>rect.bottom+50)return;
    if(g.lastX<rect.left+35)nav.scrollLeft-=16;else if(g.lastX>rect.right-35)nav.scrollLeft+=16;
    const others=seats.filter(s=>s.dataset.seatId!==g.playerId),ids=others.map(s=>s.dataset.seatId!);
    const index=others.filter(s=>{const r=s.getBoundingClientRect();return g.lastX>r.left+r.width/2;}).length;
    ids.splice(index,0,g.playerId);
    const valid=canReorderSeats(this.state().room!,ids);g.ghost.dataset.blocked=String(!valid);
    if(valid && ids.some((id,i)=>id!==g.order[i])){g.order=ids;this.place(ids);}
  }
  private place(ids:string[]):void {
    const seats=this.seats(),nav=seats[0]?.parentElement;if(!nav)return;
    for(const id of ids){const seat=seats.find(s=>s.dataset.seatId===id);if(seat){nav.appendChild(seat);seat.classList.toggle('seat-drag-placeholder',id===this.gesture?.playerId && !!this.gesture.dragging);}}
  }
  cancel():void {
    const g=this.gesture;this.gesture=null;this.pending=null;
    g?.ghost?.remove();
    if(g && this.root.hasPointerCapture(g.id))this.root.releasePointerCapture(g.id);
    const seats=this.seats(),nav=seats[0]?.parentElement;
    for(const p of this.state().room?.players ?? []){const seat=seats.find(s=>s.dataset.seatId===p.id);if(seat){seat.classList.remove('seat-drag-placeholder');nav?.appendChild(seat);}}
  }
}
